import { isJiraContext } from "../../api/api";

const isBodyEmpty = (html: string) => {
  if (!html || typeof html !== "string") return true;
  const textOnly = html.replace(/<[^>]*>/g, "").trim();
  return textOnly === "";
};

// Validate email receivers format: multiple values must be separated by commas
// Valid: "{{contact.email}}, hoang@gmail.com" or "hoang@gmail.com"
// Invalid: "{{contact.email}} hoang@gmail.com" (space without comma)
const isValidReceiversFormat = (receivers: string) => {
  if (!receivers || typeof receivers !== "string") return false;
  const trimmed = receivers.trim();
  if (trimmed === "") return false;

  // Split by comma and check each part
  const parts = trimmed
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return false;

  // Check if there are multiple items without proper comma separation
  // by detecting patterns like "}} " followed by text or email without comma
  const invalidPattern = /\}\}\s+[^,\s]|@[^\s,]+\s+[^,\s@]+@/;
  if (invalidPattern.test(trimmed)) return false;

  // Check each part is either a variable or a valid email format
  const variablePattern = /^\{\{[^}]+\}\}$/;
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  for (const part of parts) {
    if (!variablePattern.test(part) && !emailPattern.test(part)) {
      // Allow mixed content like "text {{var}}" within a single comma-separated part
      // but reject if it looks like multiple unseparated values
      const hasMultipleVariables = (part.match(/\{\{/g) || []).length > 1;
      const hasMultipleEmails = (part.match(/@[^\s@]+\.[^\s@]+/g) || []).length > 1;
      if (hasMultipleVariables || hasMultipleEmails) return false;
    }
  }

  return true;
};

export const RuleValidator = (rule) => {
  const isJira = isJiraContext();
  switch (rule.value) {
    case "status_changes":
    case "assign_person":
    case "update_column_from_to":
      return () => {
        const { data }: any = rule;
        if (data.from.value && data.to.value) {
          return (
            data.from.value !== "" && data.to.value !== "" && data.from.value !== data.to.value
          );
        }
        return false;
      };
    case "stage_changes":
    case "person_assigned":
    case "sale_moved_to_funnel":
    case "move_sale_to_funnel":
    case "change_status":
      return () => {
        const { data }: any = rule;
        if (data.to.value) {
          return data.to.value !== "";
        }
        return false;
      };
    case "clear_assignees":
      return () => {
        const { data }: any = rule;
        if (data.from.value) {
          return data.from.value !== "";
        }
        return false;
      };
    case "send_to_slack_channel":
      return () => {
        const { data }: any = rule;
        return data.channelId !== "" && (data.message ?? "").trim() !== "";
      };
    case "send_email":
      return () => {
        const { data }: any = rule;
        const receivers = (data.receivers ?? "").trim();
        const subject = (data.subject ?? "").trim();
        const body = data.body ?? "";
        return isValidReceiversFormat(receivers) && subject !== "" && !isBodyEmpty(body);
      };
    case "column_changes":
    case "contact_column_changes":
      return () => {
        const { data }: any = rule;
        if (data.to.value) {
          return data.to.value !== "";
        }
        return false;
      };
    case "update_column_to":
      return () => {
        const { data }: any = rule;
        if (data.to?.value) {
          return data.fieldId !== "" && data.to.value !== "";
        }
        return false;
      };
    case "mailchimp_sync_contact":
    case "contact2mailchimp_sync_contact":
      return () => {
        const { data }: any = rule;
        if (data.listId) {
          return data.listId !== "" && data.listName !== "";
        }
        return false;
      };
    case "convert_contact_to_new_sale":
      return () => {
        const { data }: any = rule;
        if (data.mappings) {
          const saleMappings = data.mappings["SALE"] || [];
          if (saleMappings.length > 0) {
            if (saleMappings[0].source.value) {
              return true;
            }
          }
        }
        return false;
      };
    case "convert_issue_to_new_sale":
    case "convert_customer_to_contact":
      return () => isJira;

    case "ai_field_prompt_completion":
      return () => {
        const { data }: any = rule;
        return (data.prompt ?? "").trim() !== "";
      };
    case "ai_field_classify_text":
      return () => {
        const { data }: any = rule;
        const hasSource = String(data.text ?? "").trim() !== "";
        const tags = data.tags || [];
        const hasTags = tags.filter((t: any) => String(t ?? "").trim() !== "").length > 0;
        return hasSource && hasTags;
      };
    case "ai_field_research":
      return () => {
        const { data }: any = rule;
        const questions = data.questions || [];
        return questions.filter((q: any) => String(q ?? "").trim() !== "").length > 0;
      };
    case "ai_field_summarize":
      return () => true;
    case "ai_field_classify_record":
      return () => {
        const { data }: any = rule;
        const tags = data.tags || [];
        return tags.filter((t: any) => String(t ?? "").trim() !== "").length > 0;
      };

    case "":
      return () => false;
    default:
      return () => true;
  }
};

export default RuleValidator;
